import { useContext } from 'react'

import {
  generateRedirectUrl, isValidEmail, isValidMobile,
} from '@domain-group/javascript-utils'

import {
  Button,
  Columns,
  ConditionalContent,
  LoadingModule,
  SaveFormModule,
} from '@domain-group/suit-theme'

import { generateUrls } from '@hooks/useBrand'
import useAgency, { useAgencyForm } from '@hooks/useAgency'
import useBillingTypes from '@hooks/useBillingTypes'

import { AgencyModel } from '@models/types'

import PageContext from '@contexts/pageContext'

import AgencyContacts from '@components/AgencyContacts'
import Contracts from '@components/Contracts'
import EntityActivity from '@components/EntityActivity'
import LinkToEntityModule from '@components/LinkToEntityModule'
import MessagePanel from '@components/MessagePanel'
import ScopedConditionalContent from '@components/ScopedConditionalContent'
import SupportUsersList from '@components/SupportUsersList'

import AgencyAddress from '../AgencyAddress'
import AgencyContact from '../AgencyContact'
import AgencyData from '../AgencyData'
import AgencyDetails from '../AgencyDetails'
import AgencyDisabledBanner from '../../../../components/AgencyDisabledBanner'
import AgencyLockedBanner from '../../../../components/AgencyLockedBanner'
import AgencyTools from '../../../../components/AgencyTools'
import BrandDetails from '../BrandDetails'
import PageLayout from '../../../../components/PageLayout'
import PaymentMethods from '../PaymentMethods'
import PricingPlan from '../PricingPlan'
import PrincipalContact from '../PrincipalContact'
import QuotesAndInvoicing from '../QuotesAndInvoicing'
import Subscription from '../Subscription'

const customRequiredFields = [
  { key: 'principal_email', validation: isValidEmail, invalidMessage: 'Invalid Email format' },
  { key: 'principal_mobile', validation: isValidMobile, invalidMessage: 'Invalid Mobile format' },
  { key: 'principal_name' },
]

const PageContent = (props: { agencyId: number }) => {
  const { agencyId } = props

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      deleteContract, updateAgency,
    },
  } = pageContext

  const agencyPayload = useAgency({ id: agencyId })
  const {
    agency,
    brand,
    callbacks: {
      manageAgencySupportTeam,
      updateAgency: updateFn,
    },
    loading,
    supportUserIds,
    updating,
  } = agencyPayload

  const {
    title: brandTitle,
    business_name: brandBusinessName,
    logo: brandLogo,
  } = brand || {}

  const brandImageUrl = brandLogo?.small || ''

  const { editBrandUrl } = generateUrls(brand)

  const { locked_by_user_id, billing_type_id } = agency as AgencyModel

  // @ts-expect-error TS2345
  const agencyFormPayload = useAgencyForm(agency, { customRequiredFields })
  const { entityState, saveEnabled } = agencyFormPayload

  useBillingTypes({
    performHttpRequests: !!agency?.id && !!billing_type_id,
    filters: {
      charges_type: 'subscription',
    },
  })

  const updateAgencyPayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: entityState,
    toastText: 'Agency Updated',
  }

  return (
    <PageLayout entityPayload={agencyPayload} activeTabBarItemKey="details">
      <Columns.Main>
        <LoadingModule loading={loading} times={3} />

        <ConditionalContent visible={!loading}>
          <ConditionalContent visible={!!locked_by_user_id}>
            <AgencyLockedBanner agencyPayload={agencyPayload} />
          </ConditionalContent>

          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <ConditionalContent visible={!agency?.zuora_account_number}>
              <MessagePanel
                description={`This agency does not have a Zuora Account Number stored
                                          which means no invoices can be generated against this agency. If
                                          this agency should be invoiced for any reason, please have the
                                          agency added to Salesforce. Reach out to the Realhub Onboarding 
                                          Specialist team to correctly link this account to Salesforce & Zuora.`}
                entityProperty
                marginBottom="large"
                title="Non Billable Agency"
              />
            </ConditionalContent>
          </ScopedConditionalContent>

          <ConditionalContent visible={!locked_by_user_id}>
            <AgencyDisabledBanner agencyPayload={agencyPayload} />

            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Brand Administrator', 'Support']}>
              <BrandDetails agencyFormPayload={agencyFormPayload} />

              <AgencyDetails agencyFormPayload={agencyFormPayload} />

              <AgencyContact agencyFormPayload={agencyFormPayload} />

              <PrincipalContact agencyFormPayload={agencyFormPayload} />

              <AgencyAddress agencyFormPayload={agencyFormPayload} />
            </ScopedConditionalContent>

            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
              <AgencyData agencyFormPayload={agencyFormPayload} />

              <PaymentMethods agencyFormPayload={agencyFormPayload} />

              <QuotesAndInvoicing agency={agency} agencyFormPayload={agencyFormPayload} />

              <EntityActivity subjectId={agencyId} subjectType="Agency" />
            </ScopedConditionalContent>

            <SaveFormModule>
              <Button
                buttonStyle="primaryCreate"
                disabled={!saveEnabled}
                loading={updating}
                // @ts-expect-error TS2322
                marginLeft="auto"
                onClick={() => updateAgency(updateAgencyPayload)}
                size="medium"
              >
                Save Details
              </Button>
            </SaveFormModule>
          </ConditionalContent>
        </ConditionalContent>
      </Columns.Main>

      <ConditionalContent visible={!locked_by_user_id}>
        <Columns.Sidebar>
          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <LinkToEntityModule
              // @ts-expect-error TS2322
              entityTitle={brandTitle}
              goToHref={`${editBrandUrl}?redirect=${generateRedirectUrl()}`}
              imageUrl={brandImageUrl}
              loading={loading}
              moduleTitle="Brand"
              secondaryText={brandBusinessName}
            />

            <AgencyTools agencyPayload={agencyPayload} />

            <SupportUsersList onClick={manageAgencySupportTeam} userIds={supportUserIds} />

            <Contracts callbacks={{ deleteContract }} ownerId={agencyId} ownerType="Agency" />

            <PricingPlan agencyPayload={agencyPayload} />

            <Subscription agencyPayload={agencyPayload} />
          </ScopedConditionalContent>

          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Brand Administrator', 'Support']}>
            <AgencyContacts agencyId={agencyId} />
          </ScopedConditionalContent>

        </Columns.Sidebar>
      </ConditionalContent>
    </PageLayout>
  )
}

export default PageContent
